// Card — THE section card: border/radius/white background, chevron+title+key
// info as the first row INSIDE the border, collapse persisted per storageKey.
// Extracted 2026-08-20 rolling the pattern (built first on Products, piloted
// on Components) out past the Spot/spots mechanism — HierarchyWorkspace's
// `Spot` uses this same shape for its own title+card branch; this is the
// version any screen can mount directly, for the sections that were never
// built through `spots=` (Projects/Function, Catalog, the Onshape screens).
//
//   storageKey — persistence key, used verbatim as `inoviu:${storageKey}` — the
//                caller owns the whole shape (Spot passes its own existing
//                `${storageKey}:spot:${spot.key}` so migrating Spot to use this
//                internally does not reset anyone's already-persisted collapse
//                state under a differently-shaped key).
//   title      — the chevron-adjacent heading text
//   count      — optional muted "(N)" after the title
//   right      — optional node right-aligned in the title row
//   defaultOpen — default true, matching every existing card
//   onOpen     — fires whenever the card IS open (mount-already-open included,
//                not only the click that opens it) — same contract as Spot's
//                own onOpen, for a lazy-load-on-first-expand section.
//   applicable, naReason — OPTIONAL not-applicable pair (UX-40, 2026-08-21),
//                same two-axis contract OnshapeSection already established
//                (UI_VISION §4: availability is a DB fact, open/closed is a
//                user preference — greyed is the other axis, not a third
//                open/closed value). applicable=false renders the title
//                greyed with a closed, non-interactive chevron and `#NA`
//                (naReason as its tooltip) and never mounts `children` at
//                all — same as OnshapeSection's own applicable=false, now
//                available to any plain Card instead of only the Onshape one.
//   open, onToggle — OPTIONAL controlled-open pair (2026-08-20). Supply BOTH to
//                own the open state yourself — Card then skips its own useState
//                AND the localStorage write entirely, using exactly what you
//                pass. Omit both (the default) and behaviour is byte-identical
//                to before this pair existed. For: a caller whose open state
//                needs to react to something ELSE changing too (UsageCards'
//                cards re-check on every `productId` switch, not only on the
//                click that opens them — Card's own onOpen fires only on the
//                open-transition, which a sibling prop changing does not
//                cause). The caller then owns ITS OWN persistence the same way
//                Card's internal version does — see UsageCards.jsx.

import { useState, useEffect, useCallback } from 'react';
import SectionTitle from './SectionTitle.jsx';
import { CARD_STYLE } from '../theme-tokens.js';

export default function Card({
  storageKey, title, count, right, defaultOpen = true, onOpen,
  open: controlledOpen, onToggle: controlledToggle,
  applicable = true, naReason = null,
  // The item's picture, beside this card's title. Straight passthrough to
  // SectionTitle — Card decides nothing about it (Alex, 2026-08-27).
  icon = null,
  children,
}) {
  const controlled = controlledOpen !== undefined;
  const openKey = `inoviu:${storageKey}`;
  const [internalOpen, setInternalOpen] = useState(() => {
    const saved = localStorage.getItem(openKey);
    return saved === null ? defaultOpen : saved !== '0';
  });

  const internalToggle = useCallback(() => {
    setInternalOpen((o) => {
      const next = !o;
      localStorage.setItem(openKey, next ? '1' : '0');
      return next;
    });
  }, [openKey]);

  const open = controlled ? controlledOpen : internalOpen;
  const toggle = controlled ? controlledToggle : internalToggle;

  useEffect(() => {
    if (open) onOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <div style={CARD_STYLE}>
      <SectionTitle
        na={!applicable}
        naReason={naReason}
        collapsed={applicable ? !open : undefined}
        onToggle={applicable ? toggle : undefined}
        count={count}
        right={right}
        icon={icon}
      >{title}</SectionTitle>
      {applicable && open && children}
    </div>
  );
}
